import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { ClientAnalyticsControllerService, ClientSavedAnalyticsViewResponse, ClientAnalyticsExportHistoryResponse } from 'api-clipping';
import { BrandOption } from '../filter-bar/filter-bar.component';
import { deserializeFilterState } from '../shared/saved-view.util';

export type UtilitiesSection = 'saved-views' | 'exports';

// Screen keys map 1:1 to ANALYTICS_NAV (same screens the sidebar/filter-bar already name).
const SCREEN_LABEL_KEYS: Record<string, string> = {
	OVERVIEW: 'ANALYTICS_NAV.OVERVIEW',
	COVERAGE_DASHBOARD: 'ANALYTICS_NAV.COVERAGE_DASHBOARD',
	COVERAGE_ANALYSIS: 'ANALYTICS_NAV.COVERAGE_ANALYSIS',
	BRANDS: 'ANALYTICS_NAV.BRANDS',
	TOPICS: 'ANALYTICS_NAV.TOPICS',
	SENTIMENT: 'ANALYTICS_NAV.SENTIMENT',
	MEDIA: 'ANALYTICS_NAV.MEDIA',
};

interface SavedViewItem {
	id: string;
	title: string;
	screen: string;
	screenLabel: string;
	location: string;
	badges: string[];
	updatedAt: string;
	raw: ClientSavedAnalyticsViewResponse;
}

interface ExportItemView {
	id: string;
	rowCount: number;
	periodLabel: string;
	filterSummary: string;
	createdAt: string;
}

// Ported from analytics-design/src/components/AnalyticsUtilitiesDashboard.tsx - this single design
// component serves BOTH the "Saved Views" and "Exports" menu items (DashboardContent.tsx routes
// both there with a different `defaultSection` prop), so it's one Angular component too, switched
// by `initialSection` the same way Coverage Analysis's `initialMetric` picks its starting tab.
//
// No <FilterBar/> in this design at all, so nothing was removed/reordered for that - this is the
// first screen this session with no structural adaptation needed for that reason.
//
// Exports section is now real data (GET /v1/client/analytics/recent-exports), scope-trimmed per
// instruction: only the format/columns this backend actually supports (.xlsx, one fixed column
// set - mentions, not AVE), no format picker, no column picker, no scheduled exports. There is no
// "create export" entry point here at all - that only happens from a data screen's own Export
// button (FilterBarComponent), which already has every filter it needs; this screen is read-only
// history + re-download.
@Component({
	selector: 'app-analytics-utilities',
	standalone: true,
	imports: [FormsModule, MatIconModule, KeyValuePipe, TranslateModule],
	templateUrl: './analytics-utilities.component.html',
	styleUrl: './analytics-utilities.component.scss',
})
export class AnalyticsUtilitiesComponent implements OnInit {
	@Input() initialSection: UtilitiesSection = 'saved-views';
	@Input() brandOptions: BrandOption[] = [];
	@Output() navigate = new EventEmitter<string>();
	// Opening a view is a screen+filters restore, not a simple nav target - handled by
	// AnalyticsPanelComponent (which owns both activeTab and filters), not via `navigate`.
	@Output() openSavedView = new EventEmitter<{ screen: string; filters: ReturnType<typeof deserializeFilterState>; coverageAnalysisTab?: string }>();

	activeSection: UtilitiesSection = 'saved-views';
	screenFilter = 'all';
	searchQuery = '';

	get screenOptions(): Record<string, string> {
		const out: Record<string, string> = {};
		for (const key in SCREEN_LABEL_KEYS) out[key] = this.translate.instant(SCREEN_LABEL_KEYS[key]);
		return out;
	}

	savedViewsLoading = false;
	savedViewsLoaded = false;

	exportsLoading = false;
	exportsLoaded = false;

	renameModalOpen = false;
	private renameTargetId: string | null = null;
	renameValue = '';

	toastMessage: string | null = null;
	private toastTimer?: ReturnType<typeof setTimeout>;

	openMenuId: string | null = null;

	ngOnInit(): void {
		this.activeSection = this.initialSection;
		if (this.activeSection === 'saved-views') {
			this.loadSavedViews();
		} else {
			this.loadExports();
		}
	}

	setSection(section: UtilitiesSection): void {
		this.activeSection = section;
		if (section === 'saved-views' && !this.savedViewsLoaded) {
			this.loadSavedViews();
		} else if (section === 'exports' && !this.exportsLoaded) {
			this.loadExports();
		}
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	private triggerToast(msg: string): void {
		this.toastMessage = msg;
		clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => (this.toastMessage = null), 3000);
	}

	savedViews: SavedViewItem[] = [];

	constructor(
		private readonly api: ClientAnalyticsControllerService,
		private readonly translate: TranslateService,
	) {}

	private deserialize(json: string): ReturnType<typeof deserializeFilterState> {
		return deserializeFilterState(
			json,
			this.translate.currentLang,
			this.translate.instant('ANALYTICS_DATE_PICKER.VS'),
			this.translate.instant('ANALYTICS_DATE_PICKER.NO_COMPARISON'),
			(key: string) => this.translate.instant(key),
		);
	}

	private loadSavedViews(): void {
		this.savedViewsLoading = true;
		this.api
			.getSavedAnalyticsViews()
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.savedViews = (res ?? []).map((v) => this.toSavedViewItem(v));
					this.savedViewsLoading = false;
					this.savedViewsLoaded = true;
				},
				error: () => {
					this.savedViews = [];
					this.savedViewsLoading = false;
					this.savedViewsLoaded = true;
				},
			});
	}

	private toSavedViewItem(v: ClientSavedAnalyticsViewResponse): SavedViewItem {
		const screenLabelKey = SCREEN_LABEL_KEYS[v.screen || ''];
		const screenLabel = screenLabelKey ? this.translate.instant(screenLabelKey) : v.screen || '';
		const location = v.screen === 'COVERAGE_ANALYSIS' && v.coverageAnalysisTab ? `${screenLabel} → ${v.coverageAnalysisTab}` : screenLabel;

		let periodLabel = '';
		let brandCount = 0;
		try {
			const filters = this.deserialize(v.filtersJson || '{}');
			periodLabel = filters.dateRange;
			brandCount = filters.brands?.length ?? 0;
		} catch {
			periodLabel = '';
		}

		const badges = [screenLabel];
		if (periodLabel) badges.push(periodLabel);
		if (brandCount > 0) badges.push(`${brandCount} ${this.translate.instant(brandCount === 1 ? 'ANALYTICS_GLOSSARY.BRAND' : 'ANALYTICS_GLOSSARY.BRANDS')}`);

		return {
			id: v.id!,
			title: v.name || '',
			screen: v.screen || '',
			screenLabel,
			location,
			badges,
			updatedAt: v.updatedAt ? new Date(v.updatedAt).toLocaleDateString() : '',
			raw: v,
		};
	}

	exportsList: ExportItemView[] = [];

	private loadExports(): void {
		this.exportsLoading = true;
		this.api
			.getExportHistory()
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.exportsList = (res ?? []).map((h) => this.toExportItem(h));
					this.exportsLoading = false;
					this.exportsLoaded = true;
				},
				error: () => {
					this.exportsList = [];
					this.exportsLoading = false;
					this.exportsLoaded = true;
				},
			});
	}

	private toExportItem(h: ClientAnalyticsExportHistoryResponse): ExportItemView {
		let periodLabel = '';
		let brandCount = 0;
		try {
			// Exports store the raw ClientAnalyticsFilterRequest JSON (always literal dates by
			// the time it reaches the backend), not the Saved Views FIXED/RELATIVE wrapper -
			// deserializeFilterState doesn't apply here.
			const parsed = JSON.parse(h.filtersJson || '{}');
			if (parsed.startDate && parsed.endDate) {
				periodLabel = `${parsed.startDate} – ${parsed.endDate}`;
			}
			brandCount = parsed.tagIds?.length ?? 0;
		} catch {
			/* keep defaults */
		}

		return {
			id: h.id!,
			rowCount: h.rowCount ?? 0,
			periodLabel: periodLabel || this.translate.instant('ANALYTICS_UTILITIES.UNKNOWN_PERIOD'),
			filterSummary:
				brandCount > 0
					? `${brandCount} ${this.translate.instant(brandCount === 1 ? 'ANALYTICS_GLOSSARY.BRAND' : 'ANALYTICS_GLOSSARY.BRANDS')}`
					: this.translate.instant('ANALYTICS_FILTER_BAR.ALL_BRANDS'),
			createdAt: h.createdAt ? new Date(h.createdAt).toLocaleString() : '',
		};
	}

	get filteredViews(): SavedViewItem[] {
		const q = this.searchQuery.toLowerCase();
		return this.savedViews.filter((v) => {
			const matchesScreen = this.screenFilter === 'all' || v.screen === this.screenFilter;
			const matchesSearch = !q || v.title.toLowerCase().includes(q);
			return matchesScreen && matchesSearch;
		});
	}

	badgeClass(badge: string, index: number): string {
		if (index === 0) return 'bg-blue-tint text-blue';
		return 'bg-surface-gray text-ink-muted';
	}

	toggleMenu(id: string): void {
		this.openMenuId = this.openMenuId === id ? null : id;
	}

	startRename(view: SavedViewItem): void {
		this.renameTargetId = view.id;
		this.renameValue = view.title;
		this.renameModalOpen = true;
		this.openMenuId = null;
	}

	closeRenameModal(): void {
		this.renameModalOpen = false;
		this.renameTargetId = null;
	}

	submitRename(): void {
		const name = this.renameValue.trim();
		if (!name || !this.renameTargetId) return;

		this.api
			.renameSavedAnalyticsView(this.renameTargetId, { name })
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.savedViews = this.savedViews.map((v) => (v.id === res.id ? this.toSavedViewItem(res) : v));
					this.closeRenameModal();
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_RENAMED', { name }));
				},
				error: () => {
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_RENAME_FAILED'));
				},
			});
	}

	duplicateView(view: SavedViewItem): void {
		this.openMenuId = null;
		this.api
			.createSavedAnalyticsView({
				name: `${view.title} ${this.translate.instant('ANALYTICS_UTILITIES.COPY_SUFFIX')}`,
				screen: view.raw.screen as any,
				coverageAnalysisTab: view.raw.coverageAnalysisTab,
				filtersJson: view.raw.filtersJson || '{}',
			})
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.savedViews = [this.toSavedViewItem(res), ...this.savedViews];
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_DUPLICATED', { name: res.name }));
				},
				error: () => {
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_DUPLICATE_FAILED'));
				},
			});
	}

	deleteView(view: SavedViewItem): void {
		this.openMenuId = null;
		this.api
			.deleteSavedAnalyticsView(view.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.savedViews = this.savedViews.filter((v) => v.id !== view.id);
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_DELETED', { name: view.title }));
				},
				error: () => {
					this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_DELETE_FAILED'));
				},
			});
	}

	openView(view: SavedViewItem): void {
		this.openSavedView.emit({
			screen: view.screen,
			filters: this.deserialize(view.raw.filtersJson || '{}'),
			coverageAnalysisTab: view.raw.coverageAnalysisTab,
		});
	}

	redownloadExport(item: ExportItemView): void {
		this.api
			.downloadMentionsExport(item.id)
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					const blob = new Blob([response as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, '_blank');
				},
				error: (err: HttpErrorResponse) => {
					if (err.status === 409 && err.error instanceof Blob) {
						err.error.text().then((text: string) => {
							let message = this.translate.instant('ANALYTICS_UTILITIES.TOAST_EXPORT_ROW_LIMIT');
							try {
								message = JSON.parse(text).message || message;
							} catch {
								/* keep default message */
							}
							this.triggerToast(message);
						});
					} else {
						this.triggerToast(this.translate.instant('ANALYTICS_UTILITIES.TOAST_DOWNLOAD_FAILED'));
					}
				},
			});
	}
}
